<script lang="ts" setup>
import window from '@@/bits/global'
import { __ } from '@@/bits/intl'
import OzContainedButton, {
  OzContainedButtonColorScheme,
  OzContainedButtonSizePreset,
} from '@@/library/v4/components/OzContainedButton.vue'
import OzIcon from '@@/library/v4/components/OzIcon.vue'
import OzPadletLogo, {
  OzPadletLogoColorScheme,
  OzPadletLogoSizePreset,
} from '@@/library/v4/components/OzPadletLogo.vue'
import SkipToContentLink from '@@/vuecomponents/SkipToContentLink.vue'
import { computed, onBeforeUnmount } from 'vue'

const props = defineProps<{
  showUrl: string
  title: string
  description: string
  showEmbedLogin?: boolean
  embedLoginUrl?: string
  publicKey?: string
}>()

const xEmbedLogin = computed(() => Boolean(props.showEmbedLogin && props.embedLoginUrl && props.publicKey))

// Cross-site iframes can't see the padlet.com SameSite=Lax session cookie, so login
// happens in a popup on the padlet origin. The popup mints a page_access_token and
// posts it back via `message`; we then reload with `?page_access_token=` to authenticate.
// Mirrors app/views/surface/gate/_embed_login.html.erb.
let reloaded = false
let messageHandler: ((event: MessageEvent) => void) | null = null

function reloadWithToken(token: string) {
  if (reloaded || !token) return
  reloaded = true
  stopListening()
  const url = new URL(window.location.href)
  url.searchParams.set('page_access_token', token)
  window.location.href = url.toString()
}

function startListening() {
  if (messageHandler != null) return
  messageHandler = (event: MessageEvent) => {
    if (event.origin !== window.location.origin) return
    const data = event.data ?? {}
    if (data.type === 'padlet_embed_auth' && data.token && data.publicKey === props.publicKey) {
      reloadWithToken(data.token)
    }
  }
  window.addEventListener('message', messageHandler)
}

function stopListening() {
  if (messageHandler != null) {
    window.removeEventListener('message', messageHandler)
    messageHandler = null
  }
}

function logIn(event: MouseEvent) {
  if (!props.embedLoginUrl || !props.publicKey) return
  // Listen before opening so a blocked popup that degrades to the anchor's native
  // new-tab navigation can still post its token back to this window.
  startListening()
  const popup = window.open(props.embedLoginUrl, `padlet_embed_login_${props.publicKey}`, 'width=480,height=640')
  if (popup != null) {
    // Popup opened; cancel the anchor's native navigation so we don't open twice.
    event.preventDefault()
    try {
      popup.focus()
    } catch {}
  }
  // If the popup was blocked, the button's native href/target=_blank navigation is the fallback.
}

onBeforeUnmount(stopListening)
</script>

<template>
  <div class="flex flex-col items-center justify-center h-full">
    <header :class="['fixed top-0', 'flex items-center justify-between ', 'box-border w-full', 'ps-5 pt-4']">
      <SkipToContentLink />

      <OzPadletLogo
        :size-preset="OzPadletLogoSizePreset.W48px"
        :color-scheme="OzPadletLogoColorScheme.Text300"
        :dark-mode="true"
      />
    </header>

    <main
      id="main-content"
      :class="[
        'flex flex-col items-center justify-center gap-y-4',
        'w-[360px] box-border',
        'p-4 tablet-portrait:mb-6',
        'text-light-text-100',
      ]"
      tabindex="-1"
    >
      <OzIcon name="eye_slash" :size="24" />
      <div class="px-2 text-center">
        <h1 class="text-heading-3 font-semibold">{{ title }}</h1>
        <p class="text-body-small-posts mt-2">
          {{ description }}
        </p>
      </div>
      <OzContainedButton
        v-if="xEmbedLogin"
        :href="embedLoginUrl"
        target="_blank"
        :color-scheme="OzContainedButtonColorScheme.Primary"
        :size-preset="OzContainedButtonSizePreset.H40px"
        :text="__('Log in')"
        data-testid="embed-login-button"
        @click="logIn"
      />
      <OzContainedButton
        v-else
        :href="showUrl"
        target="_blank"
        :color-scheme="OzContainedButtonColorScheme.Primary"
        :size-preset="OzContainedButtonSizePreset.H40px"
        :text="__('Open in new tab')"
        data-testid="embed-open-new-tab-button"
      />
    </main>
  </div>
</template>

<style lang="postcss">
@import '@@/styles/4/kit';

html {
  font-size: 16px;
  background-color: #47118c;
}
</style>
